<template>
  <div>
    <div class="container">
      <alert-box ref="genericAlertBox" class="spacer-m" />
    </div>

    <z-modal :open="errorModalOpen" :closable="false">
      <div class="z-title z-title--small spacer-bottom-s">
        <z-icon :icon="['far', 'times-circle']" size="medium" />
        Não foi possível carregar as informações do painel
      </div>
      <p class="z-text spacer-bottom-s">
        Recarregue a página para tentar novamente.
      </p>
      <p class="z-text">
        Caso o erro persista, entre em contato com o Quero Pago através do telefone
        <strong class="modal-phone">0800 940 0844</strong> ou através do <strong>Whatsapp</strong>
        <a href="https://api.whatsapp.com/send?phone=558002917878" target="_blank" class="z-link">clicando aqui</a>.
      </p>
    </z-modal>
  </div>
</template>

<style lang="scss" scoped>
.modal-phone {
  white-space: nowrap;
}
</style>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import ConfigsService from 'services/ConfigsService';
import AlertBox from 'components/zilla/alert-box';
import AppConfig from 'utils/app-config';
import Promise from 'bluebird';

export default {
  components: {
    AlertBox,
  },
  data() {
    return {
      errorModalOpen: false,
    };
  },
  computed: {
    ...mapState([
      'student',
      'currentBilling',
    ]),
    ...mapGetters([
      'pendingApplication',
      'pendingValidation',
      'accountNeedsConfiguration',
      'canDischargeCurrentContract',
    ]),
  },
  mounted() {
    this.$pageLoader.wrap(this.initializeAppState(), 'Carregando painel')
      .then(() => this.initializeGambitTracker())
      .catch((error) => {
        if (error && error.response && error.response.status === 401 && AppConfig.get('standalone')) return;

        this.errorModalOpen = true;
        const impersonating = !!AppConfig.get('impersonatingUserIdFromQb') || !!AppConfig.get('impersonating_user_id');
        this.$rollbar.error(`initializeAppState(): ${error} | impersonating: ${impersonating}`);
      });
  },
  methods: {
    ...mapActions([
      'loadConfigs',
      'loadStudent',
      'loadBillings',
      'loadCurrentContract',
      'loadCurrentBill',
      'loadOverdueBills',
    ]),
    initializeAppState() {
      return this.initialLoad()
        .then(() => Promise.all([
          this.loadConfigs(),
          this.loadStudent(),
          this.loadBillings(),
        ]))
        .then(() => Promise.all([
          this.loadCurrentContract(),
          this.loadCurrentBill(),
          this.loadOverdueBills(),
        ]))
        .then(() => {
          this.checkImpersonation();
          this.initializeTracker();
          this.checkInitialRoute();
          this.$emit('initialized');
        });
    },
    checkImpersonation() {
      if (!!AppConfig.get('impersonatingUserIdFromQb') || !!AppConfig.get('impersonating_user_id')) {
        this.$refs.genericAlertBox.show(
          'warning',
          'Impersonação ativada em modo leitura',
          'Você está impersonando um aluno. Neste modo, não é possível realizar pagamentos, cadastrar cartões, '
          + 'alterar forma de pagamento, alterar dia do vencimento ou até efetuar o onboarding. Apenas o aluno pode '
          + 'efetuar essas ações.',
        );
      }
    },
    initializeTracker() {
      const impersonatorId = AppConfig.get('impersonatingUserIdFromQb') || AppConfig.get('impersonating_user_id');
      const trackerContext = {
        studentId: this.student.id,
        institutionId: this.currentBilling.institution_id,
        institutionName: this.currentBilling.institution_name,
        billingId: this.currentBilling.id,
        url: window.location.href,
        routePath: this.$route.path,
        routeParams: this.$route.params,
        routeQuery: this.$route.query,
        impersonating: !!impersonatorId,
        impersonatingUserId: impersonatorId,
      };
      this.$tracker.initialize(AppConfig.get('amplitude_api_key'), trackerContext);
    },
    initializeGambitTracker() {
      if (!sessionStorage.getItem('visitHash')) {
        const generatedVisitHash = this.$gambit.generateVisitHash();

        sessionStorage.setItem('visitHash', generatedVisitHash);

        this.$gambit.trackVisit({
          baseUserId: this.student.qb_id,
          visitableType: 'load_panel',
          visitHash: generatedVisitHash,
        });
      }
    },
    initialLoad() {
      return ConfigsService.loadConfig().then((config) => {
        AppConfig.merge(config);
        this.$rollbar.initialize(AppConfig);
      });
    },
    checkInitialRoute() {
      if (!this.student.onboarding_done) {
        this.$router.replace({ name: 'onboarding' });
      } else if (!this.currentBilling.configured) {
        this.$router.replace({ name: 'payment-configuration' });
      } else if (this.$route.matched.length === 0) {
        this.$router.replace({ name: 'panel' });
      }
    },
  },
};
</script>
